// import { OnSubmitData } from '@/hooks/forgotPassword';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import Button from '@/components/common/Button';
import { useState } from 'react';
import Link from 'next/link';
import ErrorMessage from '@/components/common/ErrorMessage';
import { isSubmittedFieldValid } from '@/utilities/isSubmittedFieldValid';
import clsx from 'clsx';

interface ForgotPasswordInputs {
  emailAddress: string;
}

export default function ForgotPasswordForm() {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordInputs>();
  const [shopifyError, setShopifyError] = useState<string>('');
  const [isSuccesseful, setIsSuccesseful] = useState<boolean>(false);

  async function forgotPassword(data: { emailAddress: string }) {
    if (isSuccesseful) return;
    fetch('/api/auth/recover', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: data.emailAddress
      })
    })
      .then(async (res) => {
        setIsSuccesseful(true);
      })
      .catch((err) => {
        console.error(err);
        setShopifyError('Invalid email');
      });
  }

  return (
    <div>
      <div className="hidden lg:block mb-3">
        <Image
          src="/daily-polished-logo.svg"
          alt="logo"
          width={250}
          height={118.78}
        />
      </div>
      <div id="signup-form" className={'form'}>
        <form onSubmit={handleSubmit(forgotPassword)}>
          <p className="text-lg lg:text-lg lg:mt-10 font-[600] uppercase mb-2">
            Full size beauty at sample size prices.
          </p>
          <p>
            Get up to 60% off full size beauty products. Try something new - without
            the sample size waste.
          </p>
          <hr className="border border-b-0 w-full border-onyx my-5" />
          <h1 className="text-2xl lg:text-3xl mt-1 tracking-[0.01em]">
            Forgot your password?
          </h1>
          {shopifyError && (
            <>
              <hr className="border border-b-1 w-full border-silver my-5" />
              <div className="flex items-center my-4">
                <Image
                  src="/exclamation.svg"
                  alt="Correct"
                  width={15}
                  height={15}
                />
                <span className="ml-4">
                  <ErrorMessage message={shopifyError} />
                </span>
              </div>
            </>
          )}
          {isSuccesseful ? (
            <>
              <div className="mt-4 tracking-wide mb-12">
                <span className="mr-2">
                  <Image
                    src="/exclamation.svg"
                    alt="Correct"
                    width={15}
                    height={15}
                  />
                </span>
                Check your inbox! We&apos;ve just sent you an email with your
                reset password link. Make it quick, you&apos;ve got 24 hours.
              </div>
              <Link href="/login">
                <div className="bg-onyx text-white flex justify-center mt-1 uppercase cursor-pointer">
                  <Button className="bg-onyx text-white">SIGN IN</Button>
                </div>
              </Link>
            </>
          ) : (
            <>
              <p className="my-4">
                Enter your details and we&apos;ll send you a link to reset your
                password.
              </p>
              <div className="relative">
                <input
                  className={clsx("border focus:outline-none focus:border-onyx pl-3 py-3 w-full my-3", {
                    'border-[#E47869] focus:border-[#E47869]':
                      errors.emailAddress,
                    'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                      isSubmittedFieldValid('emailAddress', errors) &&
                      !errors.emailAddress &&
                      !shopifyError
                  })}
                  type="text"
                  placeholder="Email"
                  {...register('emailAddress', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i,
                      message: 'This field must be a valid email.'
                    }
                  })}
                />
                <span
                  className={clsx('absolute right-3 top-6 scale-125', {
                    'opacity-0':
                      !isSubmittedFieldValid('emailAddress', errors) ||
                      errors.emailAddress ||
                      shopifyError
                  })}
                >
                  <Image
                    src="/icons/green-check-mark.svg"
                    alt="Correct"
                    width={12}
                    height={9}
                  />
                </span>
              </div>
              {errors.emailAddress && (
                <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
                  <span className="mr-4">
                    <ErrorMessage message={errors.emailAddress.message || ''} />
                  </span>
                  <Image
                    src="/exclamation.svg"
                    alt="Correct"
                    width={15}
                    height={15}
                  />
                </div>
              )}
              <div className="bg-onyx text-white flex justify-center mt-1 uppercase">
                <Button className="bg-onyx text-white">NEXT</Button>
              </div>
            </>
          )}
        </form>
      </div>
      <div className="mt-6 uppercase">
        Don&apos;t have an account?{' '}
        <Link href="/membership" passHref>
          <a className="underline">
            Join now
          </a>
        </Link>
      </div>
    </div>
  );
}
